<template>
  <!-- Where used tab: every link of the system across GDPR and GRC, moving them, and retiring the system (US 9370) -->
  <div v-if="loaded" class="wu-page">
    <div class="wu-impact" :class="{ 'is-retired': retired }">
      <span class="wu-impact__icon"><font-awesome-icon :icon="retired ? 'archive' : 'exchange-alt'"/></span>
      <div class="wu-impact__text">
        <h3 v-if="retired">{{ $t('system_where_used.retired_title', { date: retiredDate }) }}</h3>
        <h3 v-else>{{ $tc('system_where_used.impact_title', total, { name: system.name, count: total }) }}</h3>
        <p>{{ retired ? $t('system_where_used.retired_text') : $t('system_where_used.impact_text') }}</p>
      </div>
      <template v-if="canEdit">
        <button v-if="retired" type="button" class="wu-impact__btn is-light" qid="system-reactivate" @click="reactivate">
          <font-awesome-icon icon="rotate-left"/>{{ $t('system_where_used.reactivate') }}
        </button>
        <template v-else>
          <button type="button" class="wu-impact__btn" qid="system-retire" @click="$refs['retire-modal'].show()">
            <font-awesome-icon icon="archive"/>{{ $t('system_where_used.retire') }}
          </button>
          <button type="button" class="wu-impact__btn is-light" :disabled="!total" qid="system-move-links" @click="openMove">
            <font-awesome-icon icon="exchange-alt"/>{{ $t('system_where_used.move') }}
          </button>
        </template>
      </template>
    </div>

    <div class="wu-toolbar">
      <div class="v2-seg" role="tablist">
        <span v-for="item in filters"
            :key="item.key"
            role="tab"
            :class="{ on: filter === item.key }"
            :qid="'where-used-filter-' + item.key"
            @click="filter = item.key">
          {{ $t('system_where_used.filters.' + item.key) }}
          <i class="pm-tabs__count">{{ item.count }}</i>
        </span>
      </div>
    </div>

    <div class="wu-lanes" :style="{ gridTemplateColumns: 'repeat(' + Math.max(visibleLanes.length, 1) + ', minmax(0, 1fr))' }">
      <section v-for="lane in visibleLanes" :key="lane.key" class="v2-card wu-lane" :qid="'where-used-lane-' + lane.key">
        <div class="wu-lane__head">
          <span class="wu-lane__icon" :class="'is-' + lane.module"><font-awesome-icon :icon="lane.icon"/></span>
          <div>
            <b>{{ $t('system_where_used.lanes.' + lane.key) }}</b>
            <small>{{ lane.sub }}</small>
          </div>
          <span class="wu-mod" :class="'is-' + lane.module">{{ lane.module === 'grc' ? 'GRC' : 'GDPR' }}</span>
        </div>
        <template v-for="group in lane.groups">
          <div v-if="group.title" :key="lane.key + group.title" class="wu-subh">{{ group.title }}</div>
          <div v-for="item in group.items" :key="lane.key + group.title + item.link_id" class="wu-item">
            <router-link v-if="item.route" :to="item.route" class="wu-item__name">{{ item.name }}</router-link>
            <span v-else class="wu-item__name" :class="{ 'is-sens': item.sensitive }">{{ item.name }}</span>
            <small v-if="item.detail">{{ item.detail }}</small>
          </div>
        </template>
        <div v-if="!lane.count" class="wu-empty">{{ $t('system_where_used.empty') }}</div>
      </section>
    </div>

    <!-- Move links to another system -->
    <b-modal ref="move-modal" hide-footer size="lg" @hidden="resetMove">
      <div class="d-block">
        <h4>{{ $t('system_where_used.move_title', { name: system.name }) }}</h4>
        <p class="text-normal">{{ $t('system_where_used.move_text') }}</p>
      </div>
      <b-form-group>
        <label>{{ $t('system_where_used.move_target') }}<span class="mandatory"> *</span></label>
        <multiselect
            v-model="target"
            :options="targets"
            track-by="id"
            label="name"
            :internal-search="false"
            :select-label="$t('system.dropdown_select_label')"
            :selected-label="$t('system.dropdown_selected_label')"
            :deselect-label="$t('system.dropdown_deselect_label')"
            :placeholder="$t('system_where_used.move_target_placeholder')"
            qid="move-links-target"
            @search-change="loadTargets"
            @input="preview"
        ></multiselect>
      </b-form-group>
      <b-form-group :label="$t('system_where_used.move_what')">
        <div v-for="lane in moveKinds" :key="lane.key" class="wu-move-kind">
          <b-form-checkbox v-model="moveSelection" :value="lane.key" :disabled="!lane.count" @change="preview">
            {{ $t('system_where_used.kinds.' + lane.key) }} <span class="text-muted">({{ lane.count }})</span>
          </b-form-checkbox>
          <small v-if="previewResult && previewResult[lane.key]" class="wu-move-kind__result">
            {{ $t('system_where_used.move_preview', previewResult[lane.key]) }}
          </small>
        </div>
      </b-form-group>
      <b-form-checkbox v-model="retireAfter" qid="move-links-retire">{{ $t('system_where_used.move_retire', { name: system.name }) }}</b-form-checkbox>
      <p class="text-muted mt-2 mb-0"><small>{{ $t('system_where_used.move_note') }}</small></p>
      <div class="text-right">
        <b-button class="mt-3" variant="secondary" @click="$refs['move-modal'].hide()">{{ $t('systems.delete.cancel') }}</b-button>
        <b-button class="mt-3 ml-3"
            variant="success"
            :disabled="!target || !moveSelection.length || moving"
            qid="move-links-submit"
            @click="submitMove">
          {{ $t('system_where_used.move_submit') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Retire -->
    <b-modal ref="retire-modal" hide-footer>
      <div class="d-block text-center">
        <h4>{{ $t('system_where_used.retire_title', { name: system.name }) }}</h4>
      </div>
      <p class="text-normal text-center">{{ $tc('system_where_used.retire_text', total, { count: total }) }}</p>
      <div class="text-center">
        <b-button class="mt-3" variant="secondary" @click="$refs['retire-modal'].hide()">{{ $t('systems.delete.cancel') }}</b-button>
        <b-button class="mt-3 ml-3" variant="success" qid="retire-submit" @click="retire">{{ $t('system_where_used.retire') }}</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import moment from 'moment';

  export default {
    name: 'SystemWhereUsedTab',
    props: {
      system: { type: Object, required: true },
      // An action from the page menu to run once the links are loaded: move, retire or reactivate
      startWith: { type: String, default: '' }
    },
    data: function() {
      return {
        loaded: false,
        kinds: [],
        lanes: {},
        total: 0,
        filter: 'all',
        targets: [],
        target: null,
        moveSelection: [],
        retireAfter: false,
        previewResult: null,
        moving: false
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      canEdit() {
        return this.isGranted([this.permissions.SYSTEM_EDIT, this.permissions.SYSTEM_EDIT_OWN], this.system) && (!this.getClient.parent || this.system.partition);
      },
      retired() {
        return this.system.lifecycle_status === 'retired';
      },
      retiredDate() {
        return this.system.retired_at ? moment(this.system.retired_at).format('D MMM YYYY') : '';
      },
      count() {
        return key => (this.lanes[key] || []).length;
      },
      // Four lanes: processes and activities, contracts, data categories, and risks with controls
      allLanes() {
        let lanes = [];
        let has = key => this.kinds.includes(key);
        if (has('activities')) {
          let groups = _.groupBy(this.lanes.activities, activity => this.processPath(activity));
          lanes.push({
            key: 'activities', icon: 'project-diagram', module: 'gdpr', count: this.count('activities'),
            sub: this.$tc('system_where_used.count_activities', this.count('activities'), { count: this.count('activities') }),
            groups: Object.keys(groups).sort().map(title => ({
              title: title,
              items: groups[title].map(activity => ({ link_id: activity.link_id, name: activity.name, route: { name: 'activities.view', params: { slug: activity.slug } } }))
            }))
          });
        }
        if (has('contracts')) {
          lanes.push({
            key: 'contracts', icon: 'file-signature', module: 'gdpr', count: this.count('contracts'),
            sub: this.$tc('system_where_used.count_contracts', this.count('contracts'), { count: this.count('contracts') }),
            groups: [{ title: '', items: this.lanes.contracts.map(contract => ({
              link_id: contract.link_id, name: contract.name, route: { name: 'contracts.view', params: { slug: contract.slug } },
              detail: [contract.covers ? this.$t('systems_overview.covers.' + contract.covers) : this.$t('systems_overview.covers.none')]
                .concat(contract.countries.length ? [contract.countries.join(', ')] : [])
                .concat(contract.transfers === 'Yes' ? [this.$t('system_page.where.transfers')] : []).join(' · ')
            })) }]
          });
        }
        if (has('data_categories')) {
          let sensitive = item => ['Sensitive art. 9', 'Criminal art. 10'].includes(item.classification);
          lanes.push({
            key: 'data_categories', icon: 'database', module: 'gdpr', count: this.count('data_categories'),
            sub: this.lanes.data_categories.some(sensitive)
              ? this.$tc('system_where_used.count_data', this.count('data_categories'), { count: this.count('data_categories'), sensitive: this.lanes.data_categories.filter(sensitive).length })
              : this.count('data_categories') + ' ' + this.$tc('system_page.used.data_categories', this.count('data_categories')),
            groups: [{ title: '', items: this.lanes.data_categories.map(item => ({ link_id: item.link_id, name: item.name, sensitive: sensitive(item), detail: item.classification })) }]
          });
        }
        let grcGroups = [];
        let grcCount = 0;
        ['risk_scenarios', 'measures', 'policies'].forEach(key => {
          if (has(key) && this.count(key)) {
            grcCount += this.count(key);
            grcGroups.push({ title: this.$t('system_where_used.kinds.' + key), items: this.lanes[key].map(item => ({ link_id: item.link_id, name: item.name })) });
          }
        });
        if (has('risk_scenarios') || has('measures') || has('policies')) {
          lanes.push({ key: 'grc', icon: 'shield-alt', module: 'grc', count: grcCount,
            sub: this.$tc('system_where_used.count_links', grcCount, { count: grcCount }), groups: grcGroups });
        }
        return lanes;
      },
      filters() {
        let privacy = this.allLanes.filter(lane => lane.module === 'gdpr');
        let risk = this.allLanes.filter(lane => lane.module === 'grc');
        let filters = [{ key: 'all', count: this.total }];
        if (privacy.length && risk.length) {
          filters.push({ key: 'privacy', count: _.sumBy(privacy, 'count') });
          filters.push({ key: 'risk', count: _.sumBy(risk, 'count') });
        }
        return filters;
      },
      visibleLanes() {
        if (this.filter === 'privacy') {
          return this.allLanes.filter(lane => lane.module === 'gdpr');
        }
        if (this.filter === 'risk') {
          return this.allLanes.filter(lane => lane.module === 'grc');
        }
        return this.allLanes;
      },
      moveKinds() {
        return this.kinds.map(key => ({ key: key, count: this.count(key) }));
      }
    },
    methods: {
      load() {
        let self = this;
        piincHttp.get('systems/' + this.system.slug + '/where-used', { params: { client: this.getClient.slug } }).then(function(response) {
          self.kinds = response.data.kinds;
          self.lanes = response.data.lanes;
          self.total = response.data.total;
          self.loaded = true;
          // The dialogs are inside the loaded page, so they exist after the next render
          if (self.startWith) {
            self.$nextTick(function() {
              self.start(self.startWith);
              self.$emit('started');
            });
          }
        }, function() {});
      },
      start(action) {
        if (action === 'move') {
          this.openMove();
        } else if (action === 'retire') {
          this.$refs['retire-modal'].show();
        } else if (action === 'reactivate') {
          this.reactivate();
        }
      },
      processPath(activity) {
        if (!activity.processes.length) {
          return this.$t('system_where_used.no_process');
        }
        let process = activity.processes[0];
        return (process.group ? process.group + ' › ' : '') + process.name;
      },
      openMove() {
        this.moveSelection = this.kinds.filter(key => this.count(key));
        this.loadTargets('');
        this.$refs['move-modal'].show();
      },
      resetMove() {
        this.target = null;
        this.previewResult = null;
        this.retireAfter = false;
      },
      loadTargets(query) {
        let self = this;
        piincHttp.get('systems', { params: { client: this.getClient.slug, keyword: query || undefined, per_page: 30 } }).then(function(response) {
          self.targets = response.data.items.filter(item => item.id !== self.system.id && item.client === self.system.client);
        }, function() {});
      },
      moveRequest(dryRun) {
        return piincHttp.post('systems/' + this.system.slug + '/move-links', {
          client: this.getClient.slug,
          target: this.target.id,
          kinds: this.moveSelection,
          dry_run: dryRun,
          retire: !dryRun && this.retireAfter
        });
      },
      // What would move and what the other system already has
      preview() {
        let self = this;
        this.$nextTick(function() {
          if (!self.target || !self.moveSelection.length) {
            self.previewResult = null;
            return;
          }
          self.moveRequest(true).then(function(response) {
            self.previewResult = response.data.result;
          }, function() {});
        });
      },
      submitMove() {
        let self = this;
        this.moving = true;
        this.moveRequest(false).then(function(response) {
          self.moving = false;
          self.$refs['move-modal'].hide();
          self.$toastr('success', self.$tc('system_where_used.moved', response.data.moved, { count: response.data.moved, name: response.data.target.name }));
          self.$emit('updated');
          self.load();
        }, function() {
          self.moving = false;
        });
      },
      setLifecycle(status) {
        let self = this;
        piincHttp.put('systems/' + this.system.slug, { lifecycle_status: status }).then(function(response) {
          self.$toastr('success', self.$t('systems_overview.saved'));
          if (response.data.slug && response.data.slug !== self.system.slug) {
            self.$router.replace({ name: 'systems.view', params: { slug: response.data.slug } });
            return;
          }
          self.$emit('updated');
        }, function() {});
      },
      retire() {
        this.$refs['retire-modal'].hide();
        this.setLifecycle('retired');
      },
      reactivate() {
        this.setLifecycle('in_use');
      }
    },
    created() {
      this.load();
    }
  }
</script>
